/**
 * M2 particle effects (M2 plan §6 M2-20): space events → `m2fx.json` recipes, and the continuous tool effects.
 *
 * - `readSpaceFx` reads the space event ring with its own cursor: `chipBreak` → chip sparks (thrown out of the
 *   rock's surface), `fractureSuccess` → the fracture shower, `fractureBurst` → the rock burst, `anchorCut` → a
 *   cutting flash and sparks thrown back toward the beam, `coolantVent` → the coolant vent plume (out of the part),
 *   `oreCollected` / `partSecured` → a capture glint, and `tetherstone` → the gate flicker (D-031) through
 *   `env.setGateFlicker`, stronger for more tonnes.
 * - `emitContinuous` runs the effects of held tools at rates per second: cutting sparks at the beam's end and the
 *   molten edge on the anchor while cutting, tractor motes along the tractor beam.
 * Positions are float64 world (D-011); everything is allocation-free (D-014).
 */
import type { Vec3 } from '../../core/math'
import { nextFloat } from '../../core/rng'
import { MAX_ANCHORS_PER_PART } from '../../data/m2Schemas'
import type { VfxEmitterSpec } from '../../render/contracts'
import type { NavKind, PartPool, SalvageReadout } from '../../space/contracts'
import { firstReadableSpace, SEV, SEV_MASK, type SpaceEventRing } from '../../space/events'
import { burstCount } from '../fx/emitters'
import { emitSpec, type ParticleSystem } from '../fx/particles'
import type { M2FxConfig } from './config'

/** What the effects read from the space runtimes (a narrow view of `SpaceWorld`). */
export interface SpaceFxSources {
  readonly events: SpaceEventRing
  readonly rocks: { position(kind: NavKind, index: number, out: Vec3): boolean }
  readonly salvage: { readonly readout: SalvageReadout; readonly parts: PartPool }
}

/** The environment hook the tetherstone flicker drives. */
export interface GateFlickerTarget {
  setGateFlicker(strength: number): void
}

export interface SpaceFxReader {
  /** Next space event to read. */
  cursor: number
  /** Effects triggered so far (tests, perf overlay). */
  triggered: number
  /** Last gate flicker strength requested. */
  lastFlicker: number
}

/** A reader that starts at the ring's current head (old events are not replayed). */
export function createSpaceFxReader(ring: SpaceEventRing): SpaceFxReader {
  return { cursor: ring.head, triggered: 0, lastFlicker: 0 }
}

/** Runs every emitter of `specs` once (`count` particles each, stochastically rounded). */
export function emitRecipe(
  sys: ParticleSystem,
  specs: readonly VfxEmitterSpec[],
  x: number,
  y: number,
  z: number,
  dx: number,
  dy: number,
  dz: number,
  time: number,
): void {
  for (let e = 0; e < specs.length; e++) {
    const spec = specs[e]
    emitSpec(sys, spec, burstCount(spec.count, sys.rng), x, y, z, dx, dy, dz, 0, 0, 0, time)
  }
}

/** Runs every emitter of a continuous recipe for `dt` seconds (`count` is per second). */
export function emitRate(
  sys: ParticleSystem,
  specs: readonly VfxEmitterSpec[],
  dt: number,
  x: number,
  y: number,
  z: number,
  dx: number,
  dy: number,
  dz: number,
  time: number,
): void {
  if (dt <= 0) return
  for (let e = 0; e < specs.length; e++) {
    const spec = specs[e]
    emitSpec(sys, spec, burstCount(spec.count * dt, sys.rng), x, y, z, dx, dy, dz, 0, 0, 0, time)
  }
}

/** Gate flicker strength for a tetherstone pickup of `tonnes`. */
export function gateFlickerStrength(cfg: Readonly<M2FxConfig['gateFlicker']>, tonnes: number): number {
  const s = cfg.baseStrength + cfg.strengthPerTonne * (tonnes > 0 ? tonnes : 0)
  return s < cfg.maxStrength ? s : cfg.maxStrength
}

// Module scratch (D-014).
const centre: Vec3 = { x: 0, y: 0, z: 0 }

/**
 * Reads every space event since the reader's cursor and triggers its effect at sim time `time`.
 * Allocation-free.
 */
export function readSpaceFx(reader: SpaceFxReader, sys: ParticleSystem, src: SpaceFxSources, cfg: M2FxConfig, env: GateFlickerTarget, time: number): void {
  const ring = src.events
  const fx = cfg.effects
  for (let i = firstReadableSpace(ring, reader.cursor); i < ring.head; i++) {
    const s = i & SEV_MASK
    const type = ring.type[s]
    const x = ring.x[s]
    const y = ring.y[s]
    const z = ring.z[s]
    if (type === SEV.chipBreak) {
      // Sparks leave the rock's surface: along the normal from its centre through the chip point.
      let dx = 0
      let dy = 1
      let dz = 0
      if (src.rocks.position(ring.b[s] as NavKind, ring.a[s], centre)) {
        dx = x - centre.x
        dy = y - centre.y
        dz = z - centre.z
      }
      emitRecipe(sys, fx.chipSparks, x, y, z, dx, dy, dz, time)
    } else if (type === SEV.fractureSuccess) {
      emitRecipe(sys, fx.fractureSuccess, x, y, z, 0, 1, 0, time)
    } else if (type === SEV.fractureBurst) {
      emitRecipe(sys, fx.rockBurst, x, y, z, 0, 1, 0, time)
    } else if (type === SEV.anchorCut) {
      const f = src.salvage.readout.beamFrom
      emitRecipe(sys, fx.anchorCut, x, y, z, f.x - x, f.y - y, f.z - z, time)
    } else if (type === SEV.coolantVent) {
      const p = ring.a[s]
      const parts = src.salvage.parts
      const out = p >= 0 && p < parts.capacity
      emitRecipe(sys, fx.coolantVent, x, y, z, out ? x - parts.x[p] : 0, out ? y - parts.y[p] : 1, out ? z - parts.z[p] : 0, time)
    } else if (type === SEV.oreCollected || type === SEV.partSecured) {
      emitRecipe(sys, fx.captureGlint, x, y, z, 0, 1, 0, time)
    } else if (type === SEV.tetherstone) {
      const strength = gateFlickerStrength(cfg.gateFlicker, ring.f[s])
      env.setGateFlicker(strength)
      reader.lastFlicker = strength
    } else {
      continue
    }
    reader.triggered++
  }
  reader.cursor = ring.head
}

/**
 * Continuous tool effects for `dt` seconds of sim time ending at `time`: cutting sparks and the molten anchor
 * edge while the cutting beam works a part, tractor motes along the tractor beam. Allocation-free.
 */
export function emitContinuous(sys: ParticleSystem, salvage: SpaceFxSources['salvage'], cfg: M2FxConfig, dt: number, time: number): void {
  if (dt <= 0) return
  const r = salvage.readout
  const fx = cfg.effects
  if (r.beamOn && r.part >= 0) {
    const f = r.beamFrom
    const t = r.beamTo
    emitRate(sys, fx.cuttingSparks, dt, t.x, t.y, t.z, f.x - t.x, f.y - t.y, f.z - t.z, time)
    const parts = salvage.parts
    if (r.anchor >= 0 && r.anchor < MAX_ANCHORS_PER_PART && r.part < parts.capacity && !r.spilling) {
      const a = r.part * MAX_ANCHORS_PER_PART + r.anchor
      emitRate(sys, fx.moltenEdge, dt, parts.ax[a], parts.ay[a], parts.az[a], f.x - t.x, f.y - t.y, f.z - t.z, time)
    }
  }
  if (r.tractorOn && r.tractorPart >= 0) {
    const f = r.beamFrom
    const t = r.tractorTo
    const specs = fx.tractorMotes
    for (let e = 0; e < specs.length; e++) {
      const spec = specs[e]
      const n = burstCount(spec.count * dt, sys.rng)
      // Motes appear anywhere along the beam and drift toward the ship.
      for (let k = 0; k < n; k++) {
        const u = nextFloat(sys.rng)
        emitSpec(sys, spec, 1, t.x + (f.x - t.x) * u, t.y + (f.y - t.y) * u, t.z + (f.z - t.z) * u, f.x - t.x, f.y - t.y, f.z - t.z, 0, 0, 0, time)
      }
    }
  }
}
